/* eslint-disable react/no-unstable-nested-components */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, INR, compactINR } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { TierBadge } from "@/components/TierBadge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Activity, Users, TrendingUp, MoonStar, ClipboardCopy, MessageCircle, Download, Package } from "lucide-react";
import { StarterKitCompare } from "@/components/StarterKitCompare";
import { LineChart, Line, XAxis, YAxis, Tooltip as RTooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const FT_LABELS = {
  "0_sales": "0 Sales",
  "1_sale": "1 Sale",
  "2_sales": "2 Sales",
  "3_plus": "3+ Sales",
};
const FT_COLORS = {
  "0_sales": "bg-rose-500/10 text-rose-600",
  "1_sale": "bg-amber-500/10 text-amber-700",
  "2_sales": "bg-blue-500/10 text-blue-700",
  "3_plus": "bg-emerald-500/10 text-emerald-700",
};

export default function Analytics() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [asms, setAsms] = useState([]);
  const [asmId, setAsmId] = useState("");
  const [tab, setTab] = useState("weekly");
  const [onb, setOnb] = useState({ preset: "all", from: "", to: "" });
  const canFilter = user && ["admin", "dept_lead"].includes(user.role);

  const load = () => {
    setLoading(true);
    const params = {};
    if (asmId) params.asm_id = asmId;
    if (onb.from) params.onboarded_from = onb.from;
    if (onb.to) params.onboarded_to = onb.to;
    api
      .get("/analytics/affiliate-views", { params })
      .then((r) => setData(r.data))
      .catch(() => toast.error("Failed to load analytics"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    if (canFilter) api.get("/users").then((r) => setAsms(r.data.filter((u) => u.role === "asm")));
  }, [asmId, onb.from, onb.to]); // eslint-disable-line

  return (
    <div>
      <PageHeader
        title="Analytics"
        subtitle="Segments, KPIs, and action lists for the affiliate program"
        actions={
          canFilter ? (
            <select
              data-testid="analytics-asm-filter"
              value={asmId}
              onChange={(e) => setAsmId(e.target.value)}
              className="h-10 rounded-full border border-border bg-card px-4 text-sm font-medium"
            >
              <option value="">All ASMs</option>
              {asms.map((u) => (
                <option key={u.id} value={u.id}>{u.name}</option>
              ))}
            </select>
          ) : null
        }
      />

      <div className="p-6 md:p-10">
        {!data ? (
          <div className="text-sm text-muted-foreground py-20 text-center">Crunching numbers…</div>
        ) : (
          <Tabs value={tab} onValueChange={setTab} className={`w-full transition-opacity ${loading ? "opacity-60" : ""}`}>
            <TabsList data-testid="analytics-tabs" className="rounded-full">
              <TabsTrigger value="weekly" data-testid="tab-weekly">
                <Activity className="h-3.5 w-3.5 mr-1.5" /> Weekly Dashboard
              </TabsTrigger>
              <TabsTrigger value="daily" data-testid="tab-daily">
                <TrendingUp className="h-3.5 w-3.5 mr-1.5" /> Daily Activation
              </TabsTrigger>
              <TabsTrigger value="monthly" data-testid="tab-monthly">
                <Users className="h-3.5 w-3.5 mr-1.5" /> Monthly Producers
              </TabsTrigger>
              <TabsTrigger value="inactive" data-testid="tab-inactive">
                <MoonStar className="h-3.5 w-3.5 mr-1.5" /> Inactive
              </TabsTrigger>
              <TabsTrigger value="starter_kit" data-testid="tab-starter-kit">
                <Package className="h-3.5 w-3.5 mr-1.5" /> Starter Kit
              </TabsTrigger>
            </TabsList>

            <TabsContent value="weekly" className="mt-6 space-y-6"><WeeklyView w={data.weekly} /><StarterKitCompare data={data.starter_kit_compare} compact testId="weekly-starter-kit-compare" /></TabsContent>
            <TabsContent value="starter_kit" className="mt-6"><StarterKitCompare data={data.starter_kit_compare} /></TabsContent>
            <TabsContent value="daily" className="mt-6"><DailyView d={data.daily_activation} /></TabsContent>
            <TabsContent value="monthly" className="mt-6"><OnboardingFilter value={onb} onChange={setOnb} /><MonthlyView m={data.monthly} /></TabsContent>
            <TabsContent value="inactive" className="mt-6"><InactiveView i={data.inactive} /></TabsContent>
          </Tabs>
        )}
      </div>
    </div>
  );
}

/* -------------------- WEEKLY -------------------- */
function WeeklyView({ w }) {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
        <Kpi label="Total Affiliates" value={w.total_affiliates} />
        <Kpi label="New (Last 30d)" value={w.new_affiliates_last_30d} />
        <Kpi label="Activation Rate" value={`${w.activation_rate_30d}%`} accent />
        <Kpi label="Monthly Producers" value={`${w.pct_monthly_producers}%`} />
        <Kpi label="Avg / Producer" value={w.avg_new_customers_per_monthly_producer} suffix="new" />
        <Kpi label="Avg Lifetime" value={w.avg_customers_per_affiliate_lifetime} suffix="new" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="weekly-revenue-kpis">
        <Kpi
          label="Avg LTV / Customer"
          value={compactINR(w.avg_ltv)}
          accent
          hint={`across ${(w.unique_customers_lifetime ?? 0).toLocaleString()} unique customers`}
          testId="kpi-avg-ltv"
        />
        <Kpi
          label="Avg Lifetime Sales / Affiliate"
          value={compactINR(w.avg_lifetime_sales_per_affiliate)}
          accent
          hint={`total: ${compactINR(w.total_lifetime_revenue)} across ${w.total_affiliates} affiliates`}
          testId="kpi-avg-lifetime-sales"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <TableCard title="Top 10 · New Customers This Month" rows={w.top10_by_new_customers_this_month}
          columns={[
            { key: "rank", label: "#", cell: (r, i) => i + 1 },
            { key: "name", label: "Name", cell: (r) => <NameCell aff={r} /> },
            { key: "tier", label: "Tier", cell: (r) => <TierBadge tier={r.tier} /> },
            { key: "asm_owner", label: "ASM", cell: (r) => r.asm_owner || "—" },
            { key: "new_customers_this_month", label: "New", align: "right", cell: (r) => <b>{r.new_customers_this_month}</b> },
          ]}
        />
        <TableCard title="Top 10 · Revenue (Last 90d)" rows={w.top10_by_revenue_last_90d}
          columns={[
            { key: "rank", label: "#", cell: (r, i) => i + 1 },
            { key: "name", label: "Name", cell: (r) => <NameCell aff={r} /> },
            { key: "tier", label: "Tier", cell: (r) => <TierBadge tier={r.tier} /> },
            { key: "asm_owner", label: "ASM", cell: (r) => r.asm_owner || "—" },
            { key: "revenue_last_90d", label: "Revenue", align: "right", cell: (r) => <b>{INR(r.revenue_last_90d)}</b> },
          ]}
        />
      </div>
    </div>
  );
}

/* -------------------- DAILY ACTIVATION -------------------- */
function ActivationTrend({ trend }) {
  if (!trend || trend.length === 0) return null;
  const latest = trend[trend.length - 1];
  const first = trend[0];
  const delta30 = (latest.rate_30d - first.rate_30d).toFixed(1);
  const delta60 = (latest.rate_60d - first.rate_60d).toFixed(1);
  const delta90 = (latest.rate_90d - first.rate_90d).toFixed(1);

  const SERIES = [
    { key: "rate_30d", label: "30d", color: "hsl(21 100% 50%)", delta: delta30 },
    { key: "rate_60d", label: "60d", color: "#3b82f6", delta: delta60 },
    { key: "rate_90d", label: "90d", color: "#a855f7", delta: delta90 },
  ];

  return (
    <div className="rounded-2xl border border-border/60 bg-card p-5" data-testid="activation-trend-chart">
      <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
        <div>
          <div className="text-sm font-semibold tracking-tight">Activation Trend</div>
          <div className="text-[11px] text-muted-foreground mt-0.5">Weekly cohort rates over the last 8 weeks</div>
        </div>
        <div className="flex items-center gap-3 text-[11px]">
          {SERIES.map((s) => (
            <div key={s.key} className="flex items-center gap-1.5" data-testid={`trend-legend-${s.label}`}>
              <span className="h-2 w-2 rounded-full" style={{ background: s.color }} />
              <span className="font-medium">{s.label}</span>
              <span className={`font-semibold ${Number(s.delta) >= 0 ? "text-emerald-600" : "text-rose-500"}`}>
                {Number(s.delta) >= 0 ? "+" : ""}{s.delta}pp
              </span>
            </div>
          ))}
        </div>
      </div>
      <div className="h-40" style={{ minHeight: 160 }}>
        <ResponsiveContainer width="100%" height="100%" minWidth={100} minHeight={160}>
          <LineChart data={trend} margin={{ top: 4, right: 8, bottom: 0, left: 4 }}>
            <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
            <YAxis unit="%" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} width={44} />
            <RTooltip
              contentStyle={{
                background: "hsl(var(--card))",
                border: "1px solid hsl(var(--border))",
                borderRadius: 12,
                fontSize: 12,
              }}
              formatter={(v) => `${v}%`}
            />
            {SERIES.map((s) => (
              <Line
                key={s.key}
                type="monotone"
                dataKey={s.key}
                name={s.label}
                stroke={s.color}
                strokeWidth={2}
                dot={{ r: 2.5 }}
                activeDot={{ r: 4 }}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function DailyView({ d }) {
  const [win, setWin] = useState(30);
  const rowsByAsm = win === 60 ? (d.by_asm_60 || []) : win === 90 ? (d.by_asm_90 || []) : (d.by_asm || []);
  const emptyLabel = `No affiliates joined in the last ${win} days.`;
  const cards = [
    { win: 30, rate: d.activation_rate_30d, activated: d.activated_30d, total: d.new_affiliates_last_30d, testId: "activation-rate-30" },
    { win: 60, rate: d.activation_rate_60d, activated: d.activated_60d, total: d.new_affiliates_last_60d, testId: "activation-rate-60" },
    { win: 90, rate: d.activation_rate_90d, activated: d.activated_90d, total: d.new_affiliates_last_90d, testId: "activation-rate-90" },
  ];

  const totalAffiliates = rowsByAsm.reduce((sum, asm) => sum + (asm.rows?.length || 0), 0);

  const downloadCohortCsv = () => {
    const FT_LABEL_CSV = { "0_sales": "0 SALES", "1_sale": "1 SALE", "2_sales": "2 SALES", "3_plus": "3+ SALES" };
    const rows = [];
    for (const asm of rowsByAsm) {
      for (const r of asm.rows || []) {
        rows.push({
          name: r.name || "",
          whatsapp: r.whatsapp || "",
          asm_owner: asm.asm_owner || "",
          days_since_join: r.days_since_join ?? "",
          activation_bucket: FT_LABEL_CSV[r.fast_track_status] || r.fast_track_status || "",
          sales_since_join: r.sales_last_30d ?? 0,
          new_customers_since_join: r.new_customers_last_30d ?? 0,
        });
      }
    }
    const escape = (v) => {
      const s = String(v ?? "");
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const headers = ["Name", "WhatsApp", "ASM Owner", "Days Since Join", "Activation Bucket", "Sales Since Join", "New Customers Since Join"];
    const lines = [headers.join(",")];
    for (const r of rows) {
      lines.push([r.name, r.whatsapp, r.asm_owner, r.days_since_join, r.activation_bucket, r.sales_since_join, r.new_customers_since_join].map(escape).join(","));
    }
    const csv = "\ufeff" + lines.join("\n"); // BOM so Excel opens UTF-8 correctly
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    const stamp = new Date().toISOString().slice(0, 10);
    link.href = url;
    link.download = `activation-cohort-${win}d-${stamp}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success(`Downloaded ${rows.length} affiliates`);
  };

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-border/60 bg-secondary/20 px-5 py-3 text-xs text-muted-foreground">
        <b className="text-foreground">Activation</b> = generated any sale (repeat OR new) within the window from the onboarding call date. The Fast-Track bonus (₹2,000 MRP) tracks new customers instead — see each affiliate profile.
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {cards.map((c) => {
          const active = win === c.win;
          return (
            <button
              key={c.win}
              type="button"
              onClick={() => setWin(c.win)}
              data-testid={`activation-card-${c.win}`}
              className={`text-left rounded-2xl border p-5 transition-all outline-none focus-visible:ring-2 focus-visible:ring-primary ${
                active
                  ? "border-primary/50 bg-primary/5 shadow-[0_0_0_1px_hsl(var(--primary)/0.35)]"
                  : "border-border/60 bg-card hover:border-primary/40 hover:bg-primary/5"
              }`}
            >
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{c.win}-Day Activation</div>
              <div className={`mt-2 text-4xl font-bold ${active ? "text-primary" : ""}`} data-testid={c.testId}>{c.rate}%</div>
              <div className="mt-1 text-xs text-muted-foreground">{c.activated} of {c.total} activated</div>
            </button>
          );
        })}
      </div>

      <ActivationTrend trend={d.trend || []} />

      <div className="flex items-center justify-between">
        <div className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
          {win}-Day Cohort · {totalAffiliates} affiliates
        </div>
        <button
          type="button"
          onClick={downloadCohortCsv}
          disabled={totalAffiliates === 0}
          data-testid="download-cohort-csv"
          className="inline-flex items-center gap-2 rounded-full border border-border/60 bg-card px-3 py-1.5 text-xs font-medium hover:border-primary/40 hover:bg-primary/5 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          <Download className="h-3.5 w-3.5" />
          Download this cohort
        </button>
      </div>

      {rowsByAsm.length === 0 ? (
        <div className="rounded-2xl border border-border/60 bg-card p-10 text-center text-sm text-muted-foreground">
          {emptyLabel}
        </div>
      ) : rowsByAsm.map((asm) => (
        <div key={asm.asm_owner} data-testid={`asm-block-${asm.asm_owner}`} className="rounded-2xl border border-border/60 bg-card overflow-x-auto">
          <div className="px-6 py-4 border-b border-border/60 flex flex-wrap items-center gap-3">
            <div className="text-base font-semibold tracking-tight">{asm.asm_owner}</div>
            {Object.entries(FT_LABELS).map(([k, label]) => (
              <span key={k} className={`text-xs font-semibold uppercase tracking-widest rounded-full px-2.5 py-0.5 ${FT_COLORS[k]}`}>
                {label}: {asm.counts[k]}
              </span>
            ))}
            <div className="ml-auto text-xs text-muted-foreground">{asm.rows.length} affiliates · {win}d cohort</div>
          </div>
          <Table
            rows={asm.rows}
            columns={[
              { key: "name", label: "Name", cell: (r) => <NameCell aff={r} /> },
              { key: "whatsapp", label: "WhatsApp", cell: (r) => <WhatsAppCell phone={r.whatsapp} /> },
              { key: "days_since_join", label: "Days Since Join", align: "right", cell: (r) => `${r.days_since_join}d` },
              { key: "fast_track_status", label: "Activation", cell: (r) => (
                <span className={`text-[10px] font-semibold uppercase tracking-widest rounded-full px-2 py-0.5 ${FT_COLORS[r.fast_track_status]}`}>
                  {FT_LABELS[r.fast_track_status]}
                </span>
              )},
              { key: "sales_last_30d", label: "Sales since join", align: "right", cell: (r) => <b>{r.sales_last_30d}</b> },
              { key: "new_customers_last_30d", label: "New Cx since join", align: "right", cell: (r) => (
                r.new_customers_last_30d > 0
                  ? <span className="font-semibold text-emerald-600">{r.new_customers_last_30d}</span>
                  : <span className="text-muted-foreground">0</span>
              )},
            ]}
            emptyMsg="No affiliates in this cohort."
          />
        </div>
      ))}
    </div>
  );
}

/* -------------------- ONBOARDING DATE FILTER -------------------- */
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const ONB_PRESETS = [
  { key: "all", label: "All time" },
  { key: "this_month", label: "This month" },
  { key: "last_month", label: "Last month" },
  { key: "last_30", label: "Last 30 days" },
  { key: "last_90", label: "Last 90 days" },
  { key: "this_quarter", label: "This quarter" },
  { key: "custom", label: "Custom" },
];
function presetRange(key) {
  const t = new Date();
  const y = t.getFullYear(), mo = t.getMonth();
  if (key === "this_month") return { from: iso(new Date(y, mo, 1)), to: iso(t) };
  if (key === "last_month") return { from: iso(new Date(y, mo - 1, 1)), to: iso(new Date(y, mo, 0)) };
  if (key === "last_30") return { from: iso(new Date(y, mo, t.getDate() - 30)), to: iso(t) };
  if (key === "last_90") return { from: iso(new Date(y, mo, t.getDate() - 90)), to: iso(t) };
  if (key === "this_quarter") return { from: iso(new Date(y, Math.floor(mo / 3) * 3, 1)), to: iso(t) };
  return { from: "", to: "" };
}
function OnboardingFilter({ value, onChange }) {
  const pick = (key) => onChange(key === "custom" ? { ...value, preset: key } : { preset: key, ...presetRange(key) });
  const dateCls = "h-9 rounded-full border border-border bg-card px-3 text-xs";
  return (
    <div data-testid="onboarding-filter" className="mb-6 flex flex-wrap items-center gap-2">
      <span className="text-[10px] uppercase tracking-widest text-muted-foreground mr-1">Onboarding call date</span>
      {ONB_PRESETS.map((p) => (
        <button
          key={p.key}
          data-testid={`onb-preset-${p.key}`}
          onClick={() => pick(p.key)}
          className={`h-9 rounded-full px-4 text-xs font-medium border transition-colors ${value.preset === p.key ? "border-primary/60 bg-primary/10 text-primary" : "border-border/60 bg-card text-muted-foreground hover:text-foreground"}`}
        >
          {p.label}
        </button>
      ))}
      {value.preset === "custom" && (
        <div className="flex items-center gap-2">
          <input type="date" data-testid="onb-from-input" value={value.from} max={value.to || undefined} onChange={(e) => onChange({ ...value, from: e.target.value })} className={dateCls} />
          <span className="text-xs text-muted-foreground">to</span>
          <input type="date" data-testid="onb-to-input" value={value.to} min={value.from || undefined} onChange={(e) => onChange({ ...value, to: e.target.value })} className={dateCls} />
        </div>
      )}
      {value.preset !== "all" && (
        <span data-testid="onb-filter-note" className="text-[11px] text-muted-foreground ml-1">Affiliates without an onboarding call date are excluded</span>
      )}
    </div>
  );
}

/* -------------------- MONTHLY PRODUCERS -------------------- */
function MonthlyView({ m }) {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-4">
        <Kpi label="Total Affiliates" value={m.total_affiliates} />
        <Kpi label="New-Cx Producers" value={m.count_monthly_producers} accent hint={`${m.pct_monthly_producers}%`} />
        <Kpi label="Sale Producers" value={m.count_sale_producers} accent hint={`${m.pct_sale_producers}%`} />
        <Kpi label="Repeat-Only" value={m.count_repeat_only_producers} hint="sold but no new cx" />
        <Kpi label="Latents (90d)" value={m.count_recent_producers} />
        <Kpi label="Never Activated" value={m.count_never_activated} />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Kpi label="Total New (MTD)" value={m.total_new_customers_this_month} />
        <Kpi label="Avg New / Producer" value={m.avg_new_customers_per_monthly_producer} suffix="this month" />
        <Kpi label="Avg Customers / Affiliate (lifetime)" value={m.avg_customers_per_affiliate_lifetime} />
      </div>

      <TableCard
        title={`Monthly Producers (New Customer) · ${m.producers.length}`}
        subtitle="Acquired ≥1 NEW customer this month — program KPI"
        rows={m.producers}
        columns={[
          { key: "name", label: "Name", cell: (r) => <NameCell aff={r} /> },
          { key: "whatsapp", label: "WhatsApp", cell: (r) => <WhatsAppCell phone={r.whatsapp} /> },
          { key: "asm_owner", label: "ASM", cell: (r) => r.asm_owner || "—" },
          { key: "new_customers_this_month", label: "New (MTD)", align: "right", cell: (r) => <b>{r.new_customers_this_month}</b> },
          { key: "total_customers_lifetime", label: "Lifetime", align: "right", cell: (r) => r.total_customers_lifetime },
        ]}
      />

      <TableCard
        title={`Monthly Producers (Generated A Sale) · ${m.sale_producers.length}`}
        subtitle="Generated ≥1 sale this month (repeat OR new)"
        rows={m.sale_producers}
        columns={[
          { key: "name", label: "Name", cell: (r) => <NameCell aff={r} /> },
          { key: "whatsapp", label: "WhatsApp", cell: (r) => <WhatsAppCell phone={r.whatsapp} /> },
          { key: "asm_owner", label: "ASM", cell: (r) => r.asm_owner || "—" },
          { key: "sales_this_month", label: "Sales (MTD)", align: "right", cell: (r) => <b>{r.sales_this_month}</b> },
          { key: "revenue_this_month", label: "Revenue (MTD)", align: "right", cell: (r) => INR(r.revenue_this_month) },
          { key: "new_customers_this_month", label: "New (MTD)", align: "right", cell: (r) => (
            r.new_customers_this_month > 0
              ? <span className="font-semibold text-emerald-600">{r.new_customers_this_month}</span>
              : <span className="text-muted-foreground">0</span>
          )},
        ]}
      />

      <TableCard
        title={`Latent Producers · ${m.latents.length}`}
        subtitle="Had sales in last 90d, none this month — win-back candidates"
        rows={m.latents}
        columns={[
          { key: "name", label: "Name", cell: (r) => <NameCell aff={r} /> },
          { key: "whatsapp", label: "WhatsApp", cell: (r) => <WhatsAppCell phone={r.whatsapp} /> },
          { key: "asm_owner", label: "ASM", cell: (r) => r.asm_owner || "—" },
          { key: "last_sale_date", label: "Last Sale", cell: (r) => r.last_sale_date || "—" },
          { key: "total_customers_lifetime", label: "Lifetime", align: "right", cell: (r) => r.total_customers_lifetime },
        ]}
      />
    </div>
  );
}

/* -------------------- INACTIVE -------------------- */
function InactiveView({ i }) {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <Kpi label="Inactive Affiliates" value={i.inactive_count} accent />
        <Kpi label="Rule" value="90d+ dormant" small />
      </div>
      <TableCard
        title={`Inactive · ${i.rows.length}`}
        rows={i.rows}
        columns={[
          { key: "name", label: "Name", cell: (r) => <NameCell aff={r} /> },
          { key: "whatsapp", label: "WhatsApp", cell: (r) => <WhatsAppCell phone={r.whatsapp} /> },
          { key: "join_date", label: "Joined", cell: (r) => r.join_date || "—" },
          { key: "last_sale_date", label: "Last Sale", cell: (r) => r.last_sale_date || "—" },
          { key: "total_customers_lifetime", label: "Lifetime", align: "right", cell: (r) => r.total_customers_lifetime },
          { key: "asm_owner", label: "ASM", cell: (r) => r.asm_owner || "—" },
        ]}
      />
    </div>
  );
}

/* -------------------- SHARED -------------------- */
function Kpi({ label, value, accent, suffix, small, hint, testId }) {
  return (
    <div data-testid={testId} className={`rounded-2xl border p-5 ${accent ? "border-primary/40 bg-primary/5" : "border-border/60 bg-card"}`}>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className={`mt-2 font-semibold tracking-tight ${small ? "text-base" : "text-2xl"} ${accent ? "text-primary" : ""}`}>{value}</div>
      {(hint || suffix) && <div className="text-[10px] text-muted-foreground mt-1">{hint || suffix}</div>}
    </div>
  );
}

function TableCard({ title, subtitle, rows, columns }) {
  const copyCsv = () => {
    const headers = columns.filter((c) => c.key !== "rank").map((c) => c.label);
    const lines = [headers.join(",")].concat(
      rows.map((r) =>
        columns
          .filter((c) => c.key !== "rank")
          .map((c) => {
            const v = r[c.key];
            if (v === null || v === undefined) return "";
            return String(v).replace(/,/g, " ");
          })
          .join(",")
      )
    );
    navigator.clipboard.writeText(lines.join("\n"));
    toast.success("Copied to clipboard — paste in Sheets");
  };
  return (
    <div className="rounded-2xl border border-border/60 bg-card overflow-x-auto">
      <div className="px-6 py-4 border-b border-border/60 flex items-center justify-between">
        <div>
          <div className="text-sm font-semibold tracking-tight">{title}</div>
          {subtitle && <div className="text-[11px] text-muted-foreground mt-0.5">{subtitle}</div>}
        </div>
        <Button
          data-testid={`copy-csv-${title.split(" ")[0].toLowerCase()}`}
          variant="outline"
          size="sm"
          className="rounded-full h-8"
          onClick={copyCsv}
        >
          <ClipboardCopy className="h-3.5 w-3.5 mr-1.5" /> Copy CSV
        </Button>
      </div>
      <Table rows={rows} columns={columns} />
    </div>
  );
}

function Table({ rows, columns, emptyMsg = "No rows." }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="bg-secondary/30">
          <tr>
            {columns.map((c) => (
              <th key={c.key} className={`text-[10px] font-semibold uppercase tracking-widest text-muted-foreground px-5 py-3 ${c.align === "right" ? "text-right" : "text-left"}`}>
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr>
              <td colSpan={columns.length} className="px-6 py-8 text-center text-sm text-muted-foreground">{emptyMsg}</td>
            </tr>
          )}
          {rows.map((r, i) => (
            <tr key={r.affiliate_id || i} className="border-t border-border/40 hover:bg-secondary/20 transition-colors">
              {columns.map((c) => (
                <td key={c.key} className={`px-5 py-3 text-sm ${c.align === "right" ? "text-right" : "text-left"}`}>
                  {c.cell ? c.cell(r, i) : r[c.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function NameCell({ aff }) {
  if (aff.affiliate_id) {
    return <Link className="font-medium text-foreground hover:text-primary transition-colors" to={`/affiliates/${aff.affiliate_id}`}>{aff.name}</Link>;
  }
  return <span className="font-medium">{aff.name}</span>;
}

function WhatsAppCell({ phone }) {
  if (!phone) return <span className="text-muted-foreground">—</span>;
  const digits = phone.replace(/[^\d]/g, "");
  return (
    <a
      href={`https://wa.me/${digits}`}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1.5 text-xs font-mono text-foreground/80 hover:text-[#25D366] transition-colors"
    >
      <MessageCircle className="h-3 w-3" />
      {phone}
    </a>
  );
}
